import { every, each, remove, differenceWith } from 'lodash';
import { FeatureFlags, isFeatureFlagEnabled } from '@core/lib/utils/featureFlags';
import { switchCurrentAccount } from '@core/api/account';
import { NOTIFICATIONS_LIMIT } from '@core/api/notifications';
import { removeCache } from '@core/lib/serviceWorker/serviceWorker';
import { getBaseDashboardUrl } from '@core/lib/utils/location';

/**
 * @ngdoc directive
 * @name adjust.directive:navbar
 * @scope
 * @restrict E
 *
 * @description
 * Navigation bar with side menu
 *
 */
function navbar() {
  'ngInject';
  return {
    restrict: 'E',
    template: require('./navbar.html'),
    scope: {},
    controller: 'NavbarController',
    controllerAs: 'vm',
    bindToController: {
      logout: '&?',
      active: '@',
      breadcrumbs: '@',
      heading: '@',
      currentTab: '=?',
      renderBreadcrumbs: '=?',
      changeLevel: '&?',
      app: '=?',
    },
  };
}

/**
 * @ngdoc controller
 * @name adjust.controller:NavbarController
 *
 * @description
 * All the functionality of the {@link adjust.directive:navbar navbar} directive
 *
 */
function NavbarController(
  Notification,
  User,
  Account,
  Deployment,
  $scope,
  $document,
  $state,
  $window,
  $q,
  gettextCatalog,
  ProductUpdate,
  Scroll,
  ReduxActions,
) {
  'ngInject';

  const vm = this;
  const isDeploymentsActive = isFeatureFlagEnabled(FeatureFlags.FeatureDeployments);

  vm.scrollInstance = 'notifications';
  Scroll.setRemote(vm.scrollInstance, true);

  vm.app = null;
  vm.loading = false;
  vm.notificationsPage = 0;
  vm.notificationsCount = 0;
  vm.notifications = [];
  vm.deployments = [];
  vm.accounts = [];
  vm.error = false;
  vm.isActive = false;
  vm.isNotificationsVisible = false;
  vm.showOnlyNotifications = false;
  vm.language = gettextCatalog.getCurrentLanguage();
  vm.unseen = true;
  vm.isTermsPage = vm.active === 'terms';
  vm.productUpdatesUrl = 'https://www.adjust.com/product-updates/';
  vm.baseUrl = getBaseDashboardUrl();
  vm.currentEnvironment = process.env.STAGE;
  vm.shouldRenderDeployments = isDeploymentsActive;
  vm.showSidebar = {
    menu: false,
    notifications: false,
    deployments: false,
    productUpdates: false,
    userSettings: false,
  };
  vm.menuLabel = t('label.menu');

  vm.toggleSidebar = toggleSidebar;
  vm.openMenu = openMenu;
  vm.searchAccount = searchAccount;
  vm.switchAccount = switchAccount;
  vm.openProductUpdates = openProductUpdates;
  vm.openNotificationCenter = openNotificationCenter;
  vm.showNotifications = showNotifications;
  vm.fetchMoreNotifications = fetchMoreNotifications;
  vm.onLogout = onLogout;
  vm.translateString = t;

  $scope.$on('$destroy', _unbindCloseEvents);
  $scope.$on('notifications:dismiss', _handleNotificationDismiss);

  vm.$onInit = _handleInit;

  function onLogout() {
    if (vm.logout) {
      vm.logout();
    } else {
      defaultLogout();
    }
  }

  function _handleInit() {
    vm.loading = true;
    $q.all([User.getCurrent(), Account.getCurrent()])
      .then(([currentUser, currentAccount]) => {
        const isActive = currentAccount.state === 'active';
        const isSuperAdmin = currentUser.super_admin;

        vm.currentUser = currentUser;
        vm.currentAccount = currentAccount;
        vm.isSuperAdmin = isSuperAdmin;
        vm.isActive = isActive;

        return isSuperAdmin ? $q.when([]) : Account.getAccounts();
      })
      .then(accounts => {
        vm.accounts = accounts;

        return $q.all([
          Deployment.fetch(),
          Notification.getNotificationsCount(),
          ProductUpdate.getUpdates(vm.currentAccount.id, vm.currentUser.language),
        ]);
      })
      .then(([deployments, notificationsCount, productUpdates]) => {
        vm.deployments = deployments;
        vm.productUpdates = productUpdates;
        vm.notificationsCount = notificationsCount;

        if (!vm.productUpdates.length && vm.notificationsCount > 0) {
          vm.showOnlyNotifications = true;
        }

        vm.unseen = !every(vm.productUpdates, 'seen_at');
      })
      .catch(function() {
        vm.error = true;
      })
      .finally(function() {
        vm.isActive = vm.isActive || vm.deployments.length > 0;
        vm.loading = false;
      });
  }

  /**
   * Looks for accounts according to the provided term
   *
   * @param {String} term
   * @returns {Promise}
   */
  function searchAccount(term) {
    if (!term || term.length < 2) {
      return Promise.resolve([]);
    }

    return Account.queryAccounts(term)
      .then(accountList => accountList.map(item => ({ value: item.id, label: item.name })))
      .catch(() => []);
  }

  /**
   * Toggles the sidebar for `menu`, `notifications` and `deployments`
   *
   * @param {String} which
   */
  function toggleSidebar(which) {
    each(vm.showSidebar, function(sidebar, key) {
      if (key !== which) {
        vm.showSidebar[key] = false;
      }
    });

    if (which) {
      vm.showSidebar[which] = !vm.showSidebar[which];
    }

    if (vm.showSidebar[which]) {
      _bindCloseEvents();
    } else {
      _unbindCloseEvents();
    }

    if (which === vm.scrollInstance || !which) {
      _toggleNotifications();
    }

    $scope.$evalAsync();
  }

  function openMenu() {
    vm.toggleSidebar('menu');
  }

  /**
   * @param {Event} e
   * @param {Object} notification
   * @private
   */
  function _handleNotificationDismiss(e, notification) {
    if (!notification.dismissable) {
      return;
    }

    Notification.dismissNotification(notification, vm.currentAccount.id)
      .then(() => {
        remove(
          vm.notifications,
          n => n.id === notification.id && n.accountType === notification.accountType
        );
      })
      .then(() => {
        $q.all([
          Notification.getNotificationsCount({ forceUpdate: true }),
          Notification.getNotifications({ accountId: vm.currentAccount.id }, { forceUpdate: true }),
        ]).then(([count, notifications]) => {
          // Update count
          vm.notificationsCount = count;

          // Append next notification, if it's available.
          const newNotifications = differenceWith(
            notifications,
            vm.notifications,
            (n1, n2) => n1.id === n2.id && n1.accountType === n2.accountType
          );
          if (newNotifications.length) {
            vm.notifications = vm.notifications.concat(newNotifications);
          }
        });
      });
  }

  /**
   * @ngdoc method
   * @name defaultLogout
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Logout action
   *
   */
  function defaultLogout() {
    User.logout()
      .then(removeCache(/runtime/))
      .then(() => {
        $state.go('login.form');
        ReduxActions.resetGlobalState();
      });
  }

  /**
   * @ngdoc method
   * @name _handleKeyClose
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Handles sidebar close when ESC is pressed
   *
   * @private
   */
  function _handleKeyClose(e) {
    if (e.keyCode !== 27) {
      return false;
    }

    toggleSidebar();
    $scope.$digest();
  }

  /**
   * @ngdoc method
   * @name _bindCloseEvents
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Bind events for sidebar close handle: **keyup** on $document
   *
   * @private
   */
  function _bindCloseEvents() {
    $document.bind('keyup', _handleKeyClose);
  }

  /**
   * @ngdoc method
   * @name _unbindCloseEvents
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Unbind events for sidebar close handle: **keyup** on $document
   *
   * @private
   */
  function _unbindCloseEvents() {
    $document.unbind('keyup', _handleKeyClose);
  }

  /**
   * @ngdoc method
   * @name switchAccount
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Switch user to the other account
   *
   * @param {Number} accountId account id to switch to
   */
  function switchAccount(accountId) {
    switchCurrentAccount(accountId).then(function() {
      $window.location.assign('/');
    });
  }

  /**
   * @ngdoc method
   * @name openProductUpdates
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Opens up the right sidebar with product updates
   *
   */
  function openProductUpdates() {
    vm.toggleSidebar('productUpdates');

    ProductUpdate.seen(vm.productUpdates).then(function() {
      vm.unseen = false;
    });
  }

  /**
   * @ngdoc method
   * @name openNotificationCenter
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * Opens up the right sidebar with Notification center updates
   *
   */
  function openNotificationCenter() {
    vm.toggleSidebar('notifications');

    if (vm.productUpdates.length === 0) {
      return;
    }

    ProductUpdate.seen(vm.productUpdates).then(function() {
      vm.unseen = false;
    });
  }

  /**
   * @ngdoc method
   * @name showNotifications
   * @methodOf adjust.controller:NavbarController
   *
   * @description
   * toggle the notifications panel in notifications center
   *
   */
  function showNotifications() {
    vm.isNotificationsVisible = !vm.isNotificationsVisible;
  }

  /**
   * @returns {Promise}
   */
  function fetchMoreNotifications() {
    const offset = NOTIFICATIONS_LIMIT * vm.notificationsPage;

    $scope.$broadcast('scroll:updating:' + vm.scrollInstance, true);
    return $q
      .when(Notification.getNotifications({ accountId: vm.currentAccount.id, offset }))
      .then(notifications => {
        if (notifications.length) {
          vm.notifications = vm.notifications.concat(notifications);
          vm.notificationsPage += 1;
        } else {
          Scroll.setLoadedRemote(vm.scrollInstance, true);
        }

        vm.error = false;
      })
      .catch(() => {
        vm.error = true;
      })
      .finally(() => {
        $scope.$broadcast('scroll:updating:' + vm.scrollInstance, false);
      });
  }

  /**
   * @private
   */
  function _toggleNotifications() {
    if (vm.showSidebar.notifications) {
      fetchMoreNotifications();
    } else {
      vm.notifications = [];
      vm.notificationsPage = 0;
      Scroll.destroy(vm.scrollInstance);
      /**
       * NOTE: Because `Scroll.destroy` reset all flags,
       * we need to set `remote` to `true` again.
       * Also this event sets `scrollTop` to `0` (which we also needed).
       */
      $scope.$broadcast('scroll:set:remote:' + vm.scrollInstance, true);
    }
  }
}

export { navbar, NavbarController };
